/*
 * Copyright (c) 2020 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.geom

import com.macrofocus.common.math.convertDegreesToRadians
import org.mkui.geom.PathIterator.Companion.SEG_CLOSE
import org.mkui.geom.PathIterator.Companion.SEG_CUBICTO
import org.mkui.geom.PathIterator.Companion.SEG_LINETO
import org.mkui.geom.PathIterator.Companion.SEG_MOVETO
import org.mkui.geom.PathIterator.Companion.WIND_NON_ZERO
import kotlin.math.*

/**
 * A utility class to iterate over the path segments of an arc
 * through the PathIterator interface.
 *
 * @author Jim Graham
 */
internal class ArcIterator(a: Arc2D, at: AffineTransform?) : PathIterator {
    val x: Double
    val y: Double
    val w: Double
    val h: Double
    val angStRad: Double
    val affine: AffineTransform?
    var increment = 0.0
    var cv = 0.0
    var index = 0
    var arcSegs = 0
    var lineSegs = 0

    /**
     * Return the winding rule for determining the insideness of the
     * path.
     *
     * @see .WIND_EVEN_ODD
     *
     * @see .WIND_NON_ZERO
     */
    override val windingRule: Int
        get() = WIND_NON_ZERO

    /**
     * Tests if there are more points to read.
     *
     * @return true if there are more points to read
     */
    override val isDone: Boolean
        get() = index > arcSegs + lineSegs

    /**
     * Moves the iterator to the next segment of the path forwards
     * along the primary direction of traversal as long as there are
     * more points in that direction.
     */
    override operator fun next() {
        index++
    }

    /**
     * Returns the coordinates and type of the current path segment in
     * the iteration.
     * The return value is the path segment type:
     * SEG_MOVETO, SEG_LINETO, SEG_QUADTO, SEG_CUBICTO, or SEG_CLOSE.
     * A float array of length 6 must be passed in and may be used to
     * store the coordinates of the point(s).
     * Each point is stored as a pair of float x,y coordinates.
     * SEG_MOVETO and SEG_LINETO types will return one point,
     * SEG_QUADTO will return two points,
     * SEG_CUBICTO will return 3 points
     * and SEG_CLOSE will not return any points.
     *
     * @see .SEG_MOVETO
     *
     * @see .SEG_LINETO
     *
     * @see .SEG_QUADTO
     *
     * @see .SEG_CUBICTO
     *
     * @see .SEG_CLOSE
     */
    override fun currentSegment(coords: FloatArray): Int {
        if (isDone) {
            throw NoSuchElementException("arc iterator out of bounds")
        }
        var angle = angStRad
        if (index == 0) {
            coords[0] = (x + cos(angle) * w).toFloat()
            coords[1] = (y + sin(angle) * h).toFloat()
            affine?.transform(coords, 0, coords, 0, 1)
            return SEG_MOVETO
        }
        if (index > arcSegs) {
            if (index == arcSegs + lineSegs) {
                return SEG_CLOSE
            }
            coords[0] = x.toFloat()
            coords[1] = y.toFloat()
            affine?.transform(coords, 0, coords, 0, 1)
            return SEG_LINETO
        }
        angle += increment * (index - 1)
        var relx: Double = cos(angle)
        var rely: Double = sin(angle)
        coords[0] = (x + (relx - cv * rely) * w).toFloat()
        coords[1] = (y + (rely + cv * relx) * h).toFloat()
        angle += increment
        relx = cos(angle)
        rely = sin(angle)
        coords[2] = (x + (relx + cv * rely) * w).toFloat()
        coords[3] = (y + (rely - cv * relx) * h).toFloat()
        coords[4] = (x + relx * w).toFloat()
        coords[5] = (y + rely * h).toFloat()
        affine?.transform(coords, 0, coords, 0, 3)
        return SEG_CUBICTO
    }

    /**
     * Returns the coordinates and type of the current path segment in
     * the iteration.
     * The return value is the path segment type:
     * SEG_MOVETO, SEG_LINETO, SEG_QUADTO, SEG_CUBICTO, or SEG_CLOSE.
     * A double array of length 6 must be passed in and may be used to
     * store the coordinates of the point(s).
     * Each point is stored as a pair of double x,y coordinates.
     * SEG_MOVETO and SEG_LINETO types will return one point,
     * SEG_QUADTO will return two points,
     * SEG_CUBICTO will return 3 points
     * and SEG_CLOSE will not return any points.
     *
     * @see .SEG_MOVETO
     *
     * @see .SEG_LINETO
     *
     * @see .SEG_QUADTO
     *
     * @see .SEG_CUBICTO
     *
     * @see .SEG_CLOSE
     */
    override fun currentSegment(coords: DoubleArray): Int {
        if (isDone) {
            throw NoSuchElementException("arc iterator out of bounds")
        }
        var angle = angStRad
        if (index == 0) {
            coords[0] = x + cos(angle) * w
            coords[1] = y + sin(angle) * h
            affine?.transform(coords, 0, coords, 0, 1)
            return SEG_MOVETO
        }
        if (index > arcSegs) {
            if (index == arcSegs + lineSegs) {
                return SEG_CLOSE
            }
            coords[0] = x
            coords[1] = y
            affine?.transform(coords, 0, coords, 0, 1)
            return SEG_LINETO
        }
        angle += increment * (index - 1)
        var relx: Double = cos(angle)
        var rely: Double = sin(angle)
        coords[0] = x + (relx - cv * rely) * w
        coords[1] = y + (rely + cv * relx) * h
        angle += increment
        relx = cos(angle)
        rely = sin(angle)
        coords[2] = x + (relx + cv * rely) * w
        coords[3] = y + (rely - cv * relx) * h
        coords[4] = x + relx * w
        coords[5] = y + rely * h
        affine?.transform(coords, 0, coords, 0, 3)
        return SEG_CUBICTO
    }

    companion object {
        /*
     * btan computes the length (k) of the control segments at
     * the beginning and end of a cubic bezier that approximates
     * a segment of an arc with extent less than or equal to
     * 90 degrees.  This length (k) will be used to generate the
     * 2 bezier control points for such a segment.
     *
     *   Assumptions:
     *     a) arc is centered on 0,0 with radius of 1.0
     *     b) arc extent is less than 90 degrees
     *     c) control points should preserve tangent
     *     d) control segments should have equal length
     *
     *   Initial data:
     *     start angle: ang1
     *     end angle:   ang2 = ang1 + extent
     *     start point: P1 = (x1, y1) = (cos(ang1), sin(ang1))
     *     end point:   P4 = (x4, y4) = (cos(ang2), sin(ang2))
     *
     *   Control points:
     *     P2 = (x2, y2)
     *     | x2 = x1 - k * sin(ang1) = cos(ang1) - k * sin(ang1)
     *     | y2 = y1 + k * cos(ang1) = sin(ang1) + k * cos(ang1)
     *
     *     P3 = (x3, y3)
     *     | x3 = x4 + k * sin(ang2) = cos(ang2) + k * sin(ang2)
     *     | y3 = y4 - k * cos(ang2) = sin(ang2) - k * cos(ang2)
     *
     * The formula for this length (k) can be found using the
     * following derivations:
     *
     *   Midpoints:
     *     a) bezier (t = 1/2)
     *        bPm = P1 * (1-t)^3 +
     *              3 * P2 * t * (1-t)^2 +
     *              3 * P3 * t^2 * (1-t) +
     *              P4 * t^3 =
     *            = (P1 + 3P2 + 3P3 + P4)/8
     *
     *     b) arc
     *        aPm = (cos((ang1 + ang2)/2), sin((ang1 + ang2)/2))
     *
     *   Let angb = (ang2 - ang1)/2; angb is half of the angle
     *   between ang1 and ang2.
     *
     *   Solve the equation bPm == aPm
     *
     *     a) For xm coord:
     *        x1 + 3*x2 + 3*x3 + x4 = 8*cos((ang1 + ang2)/2)
     *
     *        cos(ang1) + 3*cos(ang1) - 3*k*sin(ang1) +
     *        3*cos(ang2) + 3*k*sin(ang2) + cos(ang2) =
     *        = 8*cos((ang1 + ang2)/2)
     *
     *        4*cos(ang1) + 4*cos(ang2) + 3*k*(sin(ang2) - sin(ang1)) =
     *        = 8*cos((ang1 + ang2)/2)
     *
     *        8*cos((ang1 + ang2)/2)*cos((ang2 - ang1)/2) +
     *        6*k*sin((ang2 - ang1)/2)*cos((ang1 + ang2)/2) =
     *        = 8*cos((ang1 + ang2)/2)
     *
     *        4*cos(angb) + 3*k*sin(angb) = 4
     *
     *        k = 4 / 3 * (1 - cos(angb)) / sin(angb)
     *
     *     b) For ym coord we derive the same formula.
     *
     * Since this formula can generate "NaN" values for small
     * angles, we will derive a safer form that does not involve
     * dividing by very small values:
     *     (1 - cos(angb)) / sin(angb) =
     *     = (1 - cos(angb))*(1 + cos(angb)) / sin(angb)*(1 + cos(angb)) =
     *     = (1 - cos(angb)^2) / sin(angb)*(1 + cos(angb)) =
     *     = sin(angb)^2 / sin(angb)*(1 + cos(angb)) =
     *     = sin(angb) / (1 + cos(angb))
     *
     */
        private fun btan(increment: Double): Double {
            var increment = increment
            increment /= 2.0
            return 4.0 / 3.0 * sin(increment) / (1.0 + cos(increment))
        }
    }

    init {
        w = a.width / 2
        h = a.height / 2
        x = a.x + w
        y = a.y + h
        angStRad = -convertDegreesToRadians(a.angleStart)
        affine = at
        val ext: Double = -a.angleExtent
        if (ext >= 360.0 || ext <= -360) {
            arcSegs = 4
            increment = PI / 2
            // btan(Math.PI / 2);
            cv = 0.5522847498307933
            if (ext < 0) {
                increment = -increment
                cv = -cv
            }
        } else {
            arcSegs = ceil(abs(ext) / 90.0).toInt()
            increment = convertDegreesToRadians(ext / arcSegs)
            cv = btan(increment)
            if (cv == 0.0) {
                arcSegs = 0
            }
        }
        when (a.arcType) {
            Arc2D.OPEN -> lineSegs = 0
            Arc2D.CHORD -> lineSegs = 1
            Arc2D.PIE -> lineSegs = 2
        }
        if (w < 0 || h < 0) {
            lineSegs = -1
            arcSegs = lineSegs
        }
    }
}